import { Link } from "react-router-dom";
import { CalendarDays, Newspaper } from "lucide-react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingButtons from "../components/FloatingButtons";
import PageBanner from "../components/PageBanner";
import Seo from "../components/Seo";
import { useBlogs } from "../lib/BlogsContext";
import { optimizeImg } from "../lib/img";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso.replace(" ", "T")).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
}

export default function BlogList() {
  const { posts, loading } = useBlogs();

  return (
    <div className="min-h-screen bg-white text-[#222222]" data-testid="blog-list-page">
      <Seo
        title="Blog"
        description="Çiçek bakımı, hediye fikirleri ve özel günler için ilham veren yazılar — Frida Çiçekçilik blogu."
        path="/blog"
      />
      <Header />

      <PageBanner
        eyebrow="Blog"
        title="Çiçek Dünyasından"
        subtitle="Çiçek bakımı, hediye fikirleri ve özel günler için ilham veren yazılarımız."
        breadcrumb={[{ label: "Blog" }]}
      />

      <section className="py-16 md:py-20 bg-white">
        <div className="frida-container">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] rounded-2xl bg-[#F1E4D6]" />
                  <div className="h-4 w-3/4 bg-[#F1E4D6] rounded mt-4" />
                  <div className="h-3 w-1/2 bg-[#F1E4D6] rounded mt-2" />
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20" data-testid="blog-empty">
              <div className="w-16 h-16 rounded-full bg-[#F5E9DD] flex items-center justify-center mx-auto mb-5">
                <Newspaper className="w-7 h-7 text-[#A85C34]" />
              </div>
              <p className="text-[#555555] text-lg">Henüz bir blog yazısı yayınlanmadı — çok yakında burada olacak.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="blog-list-grid">
              {posts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group block"
                  data-testid={`blog-card-${post.slug}`}
                >
                  <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-[#F8F8F8]">
                    {post.cover_image ? (
                      <img
                        src={optimizeImg(post.cover_image, 640)}
                        alt={post.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Newspaper className="w-10 h-10 text-[#D4A15C]" />
                      </div>
                    )}
                  </div>
                  <h2 className="font-serif text-xl text-[#222222] mt-4 mb-1.5 leading-snug group-hover:text-[#A85C34] transition-colors">
                    {post.title}
                  </h2>
                  {post.excerpt && (
                    <p className="text-sm text-[#555555] leading-relaxed line-clamp-2">{post.excerpt}</p>
                  )}
                  {post.published_at && (
                    <div className="flex items-center gap-1.5 text-xs text-[#888888] mt-3">
                      <CalendarDays className="w-3.5 h-3.5" />
                      {formatDate(post.published_at)}
                    </div>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
}
